import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IoArrowBack } from 'react-icons/io5';
import { passwordService } from '../../../services/passwordService';
import logo from '../../../assets/Header/magnetix.png';
import './VerifyOTP.css';

const VerifyOTP: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(165); // 2:45 = 165 seconds
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isResending, setIsResending] = useState(false);

  // Get email from location state
  const email = (location.state as any)?.email;

  // Redirect if no email provided
  useEffect(() => {
    if (!email) {
      navigate('/forgot-password', { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')} Seconds`;
  };

  const handleVerify = () => {
    const otpCode = otp.join('');

    // Validation
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }

    // Navigate to create new password page with email and OTP
    navigate('/create-new-password', {
      state: { email, verificationCode: otpCode }
    });
  };

  const handleResend = async () => {
    if (!email) return;

    setIsResending(true);
    setError('');
    setSuccess('');

    try {
      const response = await passwordService.resendOTP(email);
      setSuccess(response.message || 'OTP resent successfully!');
      setTimer(165); // Reset timer
      setOtp(['', '', '', '', '', '']); // Clear OTP inputs

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to resend OTP. Please try again.');
      console.error('Resend OTP error:', err);
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="verify-otp-container">
      <div className="verify-otp-header">
        <div className="verify-otp-logo">
          <img src={logo} alt="Magnetix Logo" className="verify-otp-logo-image" />
        </div>
      </div>

      <div className="verify-otp-card">

        <h1 className="verify-otp-title">Verify OTP</h1>
        <p className="verify-otp-subtitle">Please check your registered email.</p>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#FEE',
            border: '1px solid #EF4444',
            borderRadius: '8px',
            color: '#B91C1C',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#D1FAE5',
            border: '1px solid #10B981',
            borderRadius: '8px',
            color: '#065F46',
            fontSize: '14px'
          }}>
            {success}
          </div>
        )}

        <div className="verify-otp-otp-section">
          <p className="verify-otp-otp-instruction">
            Please enter the OTP received via <strong>{email || 'your email'}</strong>
          </p>

          <div className="verify-otp-otp-inputs">
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="verify-otp-otp-input"
              />
            ))}
          </div>

          <div className="verify-otp-timer">{formatTime(timer)}</div>
        </div>

        <button
          type="button"
          className="verify-otp-verify-btn"
          onClick={handleVerify}
        >
          Verify OTP
        </button>

        <p className="verify-otp-resend-text">
          Didn't receive the code?{' '}
          <button
            type="button"
            className="verify-otp-resend-link"
            onClick={handleResend}
            disabled={isResending || timer > 0}
          >
            {isResending ? 'Resending...' : timer > 0 ? `Resend in ${formatTime(timer)}` : 'Resend OTP'}
          </button>
        </p>
      </div>
    </div>
  );
};

export default VerifyOTP;
